import {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import VLFModal from "@/components/Modal";
import ChoiceChips from "@/components/ChoiceChips";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";

import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ForumIcon from "@mui/icons-material/Forum";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";

import {MessagesAPI} from "@/api/messages";
import {conversationDisplayName, MESSAGES_READ_EVENT} from "@/utils/messages";
import {UserAPI} from "@/api/user";

import "./index.scss";

// ── Helpers ───────────────────────────────────────────────────────────────────

const POLL_MS = 30000;

const initials = (name = "") =>
	name
		.split(" ")
		.slice(0, 2)
		.map((w) => w[0] ?? "")
		.join("")
		.toUpperCase();

const fmtTime = (ts) => {
	if (!ts) return "";
	const m = moment(ts);
	if (m.isSame(moment(), "day")) return m.format("HH:mm");
	if (m.isSame(moment().subtract(1, "day"), "day")) return "ieri";
	return m.format("DD/MM");
};

// ── Mini avatar ───────────────────────────────────────────────────────────────

function MiniAvatar({name, size = 30}) {
	return (
		<div
			className="cfab-avatar"
			style={{width: size, height: size, fontSize: size * 0.36}}
		>
			{initials(name)}
		</div>
	);
}

// ── New conversation modal (self-contained) ───────────────────────────────────
function NewConvModal({open, close, onCreated}) {
	const currentUserId = useSelector((s) => s?.user?.userId);

	const [type, setType] = useState("direct");
	const [users, setUsers] = useState([]);
	const [selectedUser, setSelectedUser] = useState(null);
	const [groupName, setGroupName] = useState("");
	const [groupMembers, setGroupMembers] = useState([]);
	const [loading, setLoading] = useState(false);

	// Load users lazily when modal opens
	useEffect(() => {
		if (!open) return;
		UserAPI.list()
			.then((data) =>
				setUsers(
					(data ?? [])
						.filter((u) => String(u.userId ?? u.id) !== String(currentUserId))
						.map((u) => ({
							value: u.userId ?? u.id,
							label:
								[u.firstName, u.lastName].filter(Boolean).join(" ") ||
								u.userName,
						})),
				),
			)
			.catch(() => {});
	}, [open, currentUserId]);

	const reset = () => {
		setType("direct");
		setSelectedUser(null);
		setGroupName("");
		setGroupMembers([]);
	};

	const handleClose = () => {
		reset();
		close();
	};

	const handleConfirm = async () => {
		setLoading(true);
		try {
			let conv;
			if (type === "direct") {
				conv = await MessagesAPI.getOrCreateDirect({
					otherUserId: selectedUser?.value,
				});
			} else {
				conv = await MessagesAPI.createGroup({
					name: groupName,
					participantIds: groupMembers.map((m) => m.value),
				});
			}
			onCreated(conv);
			handleClose();
		} catch {
			// noop
		} finally {
			setLoading(false);
		}
	};

	const canConfirm =
		type === "direct"
			? !!selectedUser
			: groupName.trim() && groupMembers.length > 0;

	return (
		<VLFModal
			open={open}
			title="Nuova conversazione"
			close={handleClose}
			confirm={handleConfirm}
			confirmDisabled={!canConfirm || loading}
			maxWidth="sm"
			content={
				<div className="cfab-newconv">
					<ChoiceChips
						options={[
							{value: "direct", label: "Diretta"},
							{value: "group", label: "Gruppo"},
						]}
						value={type}
						onChange={(value) => {
							setType(value);
							setSelectedUser(null);
							setGroupMembers([]);
						}}
					/>

					{type === "direct" ? (
						<VLFSelectNew
							options={users}
							value={selectedUser}
							onChange={setSelectedUser}
							label="Utente"
						/>
					) : (
						<>
							<VLFTextField
								label="Nome gruppo"
								value={groupName}
								onChange={(e) => setGroupName(e.target.value)}
								size="small"
							/>
							<VLFSelectNew
								options={users}
								value={groupMembers}
								onChange={setGroupMembers}
								label="Partecipanti"
								isMulti
							/>
						</>
					)}
				</div>
			}
		/>
	);
}

// ── Main FAB ──────────────────────────────────────────────────────────────────

export default function ChatFAB() {
	const navigate = useNavigate();
	const location = useLocation();
	const currentUserId = useSelector((s) => s?.user?.userId);
	const session = useSelector((s) => s?.token);

	const [open, setOpen] = useState(false);
	const [unreadState, setUnreadState] = useState({session: null, count: 0});
	const unread = unreadState.session === session ? unreadState.count : 0;
	const [conversations, setConversations] = useState([]);
	const [openNewConv, setOpenNewConv] = useState(false);

	const panelRef = useRef(null);
	const pollRef = useRef(null);
	const sessionRef = useRef(session);
	const unreadRequest = useRef(0);
	sessionRef.current = session;

	// Don't show FAB on the messages page itself
	const onMessagesPage = location.pathname.startsWith("/messages");

	// ── Poll unread count ─────────────────────────────────────────────────────

	const fetchUnread = useCallback(async () => {
		if (!session) return;
		const request = ++unreadRequest.current;
		try {
			const data = await MessagesAPI.getUnreadCount();
			if (sessionRef.current !== session || request !== unreadRequest.current)
				return;
			setUnreadState({session, count: data?.count ?? data ?? 0});
		} catch {
			// Mantiene l'ultimo conteggio disponibile della sessione corrente.
		}
	}, [session]);

	useEffect(() => {
		fetchUnread();
		pollRef.current = setInterval(fetchUnread, POLL_MS);
		window.addEventListener("focus", fetchUnread);
		window.addEventListener(MESSAGES_READ_EVENT, fetchUnread);
		return () => {
			clearInterval(pollRef.current);
			unreadRequest.current += 1;
			window.removeEventListener("focus", fetchUnread);
			window.removeEventListener(MESSAGES_READ_EVENT, fetchUnread);
		};
	}, [fetchUnread, location.pathname]);

	useEffect(() => {
		setOpen(false);
		setConversations([]);
		setOpenNewConv(false);
	}, [session]);

	// ── Load conversations when panel opens ───────────────────────────────────

	useEffect(() => {
		if (!open) return;
		let active = true;
		MessagesAPI.getConversations()
			.then((data) => active && setConversations(data ?? []))
			.catch(() => {});
		return () => {
			active = false;
		};
	}, [open, session]);

	// ── Close panel on outside click ──────────────────────────────────────────

	useEffect(() => {
		if (!open) return;
		const handleClick = (e) => {
			if (panelRef.current && !panelRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	// ── Close panel when navigating ───────────────────────────────────────────

	useEffect(() => {
		setOpen(false);
	}, [location.pathname]);

	if (onMessagesPage || (!open && !openNewConv && unread <= 0)) return null;

	const handleConvClick = (conv) => {
		navigate("/messages/" + conv.id);
		setOpen(false);
	};

	const handleConvCreated = (conv) => {
		navigate("/messages/" + conv.id);
		fetchUnread();
	};

	const top5 = [...conversations]
		.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
		.slice(0, 5);

	return (
		<>
			<div className="cfab-root" ref={panelRef}>
				{/* Panel */}
				{open && (
					<div className="cfab-panel">
						{/* Panel header */}
						<div className="cfab-panel__header">
							<span className="cfab-panel__title">
								<ForumIcon sx={{fontSize: 16}} /> Messaggi
							</span>
							<div className="cfab-panel__header-actions">
								<button
									className="cfab-panel__icon-btn"
									onClick={() => navigate("/messages")}
									title="Apri messaggi"
								>
									<OpenInFullIcon sx={{fontSize: 14}} />
								</button>
								<button
									className="cfab-panel__icon-btn"
									onClick={() => setOpen(false)}
									title="Chiudi"
								>
									<CloseIcon sx={{fontSize: 16}} />
								</button>
							</div>
						</div>

						{/* Conversation list */}
						<div className="cfab-panel__list">
							{top5.length === 0 ? (
								<div className="cfab-panel__empty">Nessuna conversazione</div>
							) : (
								top5.map((conv) => {
									const name = conversationDisplayName(conv, currentUserId);
									const unreadCount = conv.unreadCount ?? 0;
									return (
										<button
											key={conv.id}
											className="cfab-conv-row"
											onClick={() => handleConvClick(conv)}
										>
											<MiniAvatar name={name} />
											<div className="cfab-conv-row__body">
												<div className="cfab-conv-row__top">
													<span className="cfab-conv-row__name">{name}</span>
													<span className="cfab-conv-row__time">
														{fmtTime(conv.updatedAt)}
													</span>
												</div>
												{conv.lastMessage?.content && (
													<span className="cfab-conv-row__preview">
														{conv.lastMessage.content}
													</span>
												)}
											</div>
											{unreadCount > 0 && (
												<span className="cfab-conv-row__badge">
													{unreadCount > 99 ? "99+" : unreadCount}
												</span>
											)}
										</button>
									);
								})
							)}
						</div>

						{/* Panel footer */}
						<div className="cfab-panel__footer">
							<button
								className="cfab-panel__new-btn"
								onClick={() => {
									setOpen(false);
									setOpenNewConv(true);
								}}
							>
								<AddIcon sx={{fontSize: 16}} />
								Nuova conversazione
							</button>
						</div>
					</div>
				)}

				{/* FAB button */}
				<button
					className={"cfab-btn" + (open ? " cfab-btn--open" : "")}
					onClick={() => setOpen((v) => !v)}
					title="Messaggi"
				>
					<ChatBubbleOutlineIcon sx={{fontSize: 22}} />
					{unread > 0 && (
						<span className="cfab-btn__badge">
							{unread > 99 ? "99+" : unread}
						</span>
					)}
				</button>
			</div>

			<NewConvModal
				open={openNewConv}
				close={() => setOpenNewConv(false)}
				onCreated={handleConvCreated}
			/>
		</>
	);
}
